<template>
  <div class="d-flex justify-center flex-column">
    <v-select
      v-model="selectedItems"
      :items="filteredItems"
      :loading="loading"
      append-icon=""
      :return-object="returnObject"
      :multiple="multiple"
      :hide-no-data="hideNoData"
      :chips="chips"
      :item-text="itemText"
      :item-value="itemValue"
      :no-data-text="$t('common.fields.noDataAvailable')"
      @change="handleChange"
      @click:clear="clearSelection"
      :menu-props="{ closeOnContentClick: false, maxHeight: 500, ...menuProps }"
      v-bind="selectProps"
      clearable
      class="advanced-filter-select"
    >
      <template v-slot:label>
        <slot name="label">
          <span class="full-width-label">
            {{ label || searchLabel }}
            <strong v-if="required" class="red--text">*</strong>
          </span>
        </slot>
      </template>

      <!-- Search and filter area -->
      <template v-slot:prepend-item>
        <slot name="filter-options">
          <div class="filter-options pa-2">
            <v-row class="px-2 py-2 filter-row">
              <v-col :cols="filterCriteriaCols" class="py-1">
                <v-select
                  outlined
                  v-model="filterCriteria"
                  :items="availableFilterOptions"
                  :label="$t('common.filters.criteria')"
                  hide-details
                  :return-object="false"
                  item-value="value"
                  item-text="name"
                  dense
                  class="criteria-select"
                ></v-select>
              </v-col>
              <v-col :cols="12 - filterCriteriaCols" class="py-1">
                <v-text-field
                  outlined
                  v-model="searchQuery"
                  :label="searchLabel || $t('home.fields.search')"
                  @keyup.enter="applySearch"
                  @input="debouncedSearch"
                  hide-details
                  dense
                  class="search-field"
                >
                  <template v-slot:append>
                    <v-icon small @click="applySearch" class="search-icon">mdi-magnify</v-icon>
                  </template>
                </v-text-field>
              </v-col>
            </v-row>
          </div>
          <v-divider />
        </slot>
      </template>

      <!-- Selection display customization -->
      <template v-slot:selection="{ item, index }">
        <slot name="selection" :item="item" :index="index">
          <v-chip v-if="index === 0" small>
            <span>{{ getItemDisplayText(item) }}</span>
          </v-chip>
          <span v-if="index === 1" class="grey--text caption">
            (+{{ selectedItems.length - 1 }} {{ $t("common.titles.others") }})
          </span>
        </slot>
      </template>

      <!-- Item display customization -->
      <template v-slot:item="{ item, attrs }">
        <slot name="item" :item="item" :attrs="attrs">
          <div :style="{ maxWidth: itemMaxWidth + 'px' }">
            <v-checkbox
              v-if="multiple"
              :input-value="attrs.inputValue"
              color="primary"
              class="pt-2 pb-2 mt-0"
              :hide-details="true"
            >
              <template v-slot:label>
                <read-more
                  :classes="'mb-0'"
                  :text="getItemDisplayText(item)"
                  :max-chars="itemTextMaxLength"
                  :font-size="itemTextFontSize"
                />
              </template>
            </v-checkbox>
            <read-more
              v-else
              :classes="'mb-0'"
              :text="getItemDisplayText(item)"
              :max-chars="itemTextMaxLength"
              :font-size="itemTextFontSize"
            />
          </div>
        </slot>
      </template>

      <!-- Pagination -->
      <template v-slot:append-item>
        <slot name="pagination" 
              :total-pages="totalPages" 
              :is-first-page="isFirstPage" 
              :is-last-page="isLastPage" 
              :previous-page="previousPage" 
              :next-page="nextPage"
              :first-element-index="firstElementIndex"
              :last-element-index="lastElementIndex"
              :total-filtered="totalFilteredItems">
          <template v-if="pagination && totalPages > 1">
            <v-divider />
            <div class="pagination mx-1 my-2">
              <v-spacer />
              <v-btn text small :disabled="isFirstPage" @click="previousPage">
                <v-icon small>mdi-chevron-left</v-icon>
              </v-btn>
              <span style="font-size: 12px" class="mx-2 ma-0 pa-0">
                {{ firstElementIndex + 1 }} - {{ lastElementIndex }} {{ $t('common.filters.of') }} {{ totalFilteredItems }}
              </span>
              <v-btn text small :disabled="isLastPage" @click="nextPage">
                <v-icon small>mdi-chevron-right</v-icon>
              </v-btn>
            </div>
          </template>
        </slot>
      </template>
    </v-select>

    <!-- Selected items chips display -->
    <div v-if="showSelectedChips && selectedItems && selectedItems.length > 0" class="d-flex align-center justify-start chip-container">
      <v-chip
        :key="`summary-${chipGroupId}`"
        class="d-flex align-center mr-2 flex-shrink-0"
        v-bind="chipSummaryProps"
      >
        {{ totalSelectedText }}: {{ selectedItems.length }}

        <v-icon
          small
          class="ml-2"
          @click="clearSelection"
        >
          mdi-close
        </v-icon>
      </v-chip>

      <v-chip-group 
        :id="`chip-group__${chipGroupId}`" 
        show-arrows
        class="chip-scroll-container"
        style="width: 480px"
      >
        <v-chip
          v-for="(item, index) in selectedItems"
          :key="`chip-${index}-${getChipValue(item)}`"
          :close="true"
          @click:close="removeItem(index)"
          v-bind="chipProps"
          class="my-1"
        >
          {{ getItemDisplayText(item) }}
        </v-chip>
      </v-chip-group>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash';

export default {
  name: 'AdvancedFilterComponent',
  
  components: {
    ReadMore: () => import('@/components/ReadMoreComponent.vue')
  },
  
  props: {
    // Data configuration
    value: {
      type: [Array, Object, String, Number],
      default: () => []
    },
    items: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    
    // Selection configuration
    multiple: {
      type: Boolean,
      default: false
    },
    returnObject: {
      type: Boolean,
      default: true
    },
    itemText: {
      type: [String, Function],
      default: 'name'
    },
    itemValue: {
      type: [String, Function],
      default: 'id'
    },
    
    // UI configuration
    label: {
      type: String,
      default: ''
    },
    searchLabel: {
      type: String,
      default: ''
    },
    required: {
      type: Boolean,
      default: false
    },
    hideNoData: {
      type: Boolean,
      default: true
    },
    chips: {
      type: Boolean,
      default: false
    },
    showSelectedChips: {
      type: Boolean,
      default: true
    },
    chipGroupId: {
      type: String,
      default: 'items'
    },
    chipGroupWidth: {
      type: Number,
      default: 480
    },
    itemMaxWidth: {
      type: Number,
      default: 360
    },
    itemTextMaxLength: {
      type: Number,
      default: 80
    },
    itemTextFontSize: {
      type: Number,
      default: 14
    },
    totalSelectedText: {
      type: String,
      default: 'Total'
    },
    filterCriteriaCols: {
      type: Number,
      default: 4
    },
    
    // Pagination configuration
    pagination: {
      type: Boolean,
      default: true
    },
    pageSize: {
      type: Number,
      default: 10
    },
    
    // Filter configuration
    filterOptions: {
      type: Array,
      default: () => []
    },
    defaultFilterCriteria: {
      type: String,
      default: 'contain'
    },
    searchDebounceTime: {
      type: Number,
      default: 300
    },
    
    // Component props customization
    selectProps: {
      type: Object,
      default: () => ({
        outlined: false,
        dense: false,
        solo: true,
        backgroundColor: '#EAEAEA80',
        flat: true,
        hideDetails: true
      })
    },
    chipProps: {
      type: Object,
      default: () => ({})
    },
    chipSummaryProps: {
      type: Object,
      default: () => ({})
    },
    menuProps: {
      type: Object,
      default: () => ({})
    },
    
    // Remote search configuration
    remoteSearch: {
      type: Boolean,
      default: false
    }
  },
  
  data() {
    return {
      selectedItems: this.multiple ? [] : null,
      searchQuery: '',
      filterCriteria: this.defaultFilterCriteria,
      localItems: [],
      totalFilteredItems: 0,
      currentPage: 0,
      totalPages: 1,
      localFiltering: true,
      internalFilterOptions: [],
      preventSearch: false
    };
  },
  
  computed: {
    filteredItems() {
      if (this.remoteSearch) {
        return this.items;
      }
      
      if (!this.searchQuery) {
        return this.getPaginatedItems(this.items);
      }
      
      const filtered = this.items.filter(item => {
        const itemText = this.getItemDisplayText(item).toLowerCase();
        const search = this.searchQuery.toLowerCase();
        
        switch (this.filterCriteria) {
          case 'exact':
            return itemText === search;
          case 'start':
            return itemText.startsWith(search);
          case 'end':
            return itemText.endsWith(search);
          case 'contain':
          default:
            return itemText.includes(search);
        }
      });
      
      this.totalFilteredItems = filtered.length;
      this.totalPages = Math.ceil(filtered.length / this.pageSize);
      
      return this.getPaginatedItems(filtered);
    },
    
    isFirstPage() {
      return this.currentPage === 0;
    },
    
    isLastPage() {
      return this.currentPage >= this.totalPages - 1;
    },
    
    firstElementIndex() {
      return this.currentPage * this.pageSize;
    },
    
    lastElementIndex() {
      return Math.min(
        (this.currentPage + 1) * this.pageSize, 
        this.totalFilteredItems
      );
    },
    
    // Add computed property for filter options
    availableFilterOptions() {
      // Use internal options if available, otherwise use props
      return this.internalFilterOptions.length > 0 
        ? this.internalFilterOptions 
        : this.filterOptions;
    }
  },
  
  watch: {
    value: {
      handler(newVal) {
        this.selectedItems = newVal;
      },
      immediate: true
    },
    
    items: {
      handler(newItems) {
        this.localItems = [...newItems];
        this.totalFilteredItems = newItems.length;
        this.totalPages = Math.ceil(newItems.length / this.pageSize);
      },
      immediate: true
    },
    
    // Add watcher for filterOptions
    filterOptions: {
      handler(newOptions) {
        if (newOptions && newOptions.length > 0) {
          this.internalFilterOptions = [...newOptions];
        }
      },
      immediate: true
    }
  },
  
  created() {
    this.debouncedSearch = debounce(() => {
      // Only emit search for remote search and when typing
      if (this.remoteSearch && !this.preventSearch) {
        this.applySearch();
      }
    }, this.searchDebounceTime);
    
    // Initialize filter options if none provided
    if (!this.filterOptions || this.filterOptions.length === 0) {
      this.internalFilterOptions = [
        { name: this.$t('common.filters.exact'), value: 'exact' },
        { name: this.$t('common.filters.start'), value: 'start' },
        { name: this.$t('common.filters.end'), value: 'end' },
        { name: this.$t('common.filters.contain'), value: 'contain' }
      ];
    }
    
    // Set default filter criteria if not already set
    if (!this.filterCriteria) {
      this.filterCriteria = this.defaultFilterCriteria;
    }
    
    // Listen for root events to clear selection
    this.$root.$on(`clear:selected:${this.chipGroupId}`, this.clearSelection);

    // Listen for component-specific clear events
    this.$root.$on(`clear:selected:${this.chipGroupId}`, () => {
      console.log(`AdvancedFilterComponent with chipGroupId ${this.chipGroupId} received reset event`);
      this.reset();
    });
  },
  
  beforeDestroy() {
    // Clean up event listener
    this.$root.$off(`clear:selected:${this.chipGroupId}`, this.clearSelection);

    // Clean up event listeners
    this.$root.$off(`clear:selected:${this.chipGroupId}`, this.reset);
  },
  
  methods: {
    getPaginatedItems(items) {
      const start = this.currentPage * this.pageSize;
      const end = start + this.pageSize;
      return items.slice(start, end);
    },
    
    getItemDisplayText(item) {
      if (!item) return '';
      
      if (typeof this.itemText === 'function') {
        return this.itemText(item);
      }
      
      return typeof item === 'object' ? item[this.itemText] : item;
    },
    
    getChipValue(item) {
      if (!item) return '';
      
      if (typeof this.itemValue === 'function') {
        return this.itemValue(item);
      }
      
      return typeof item === 'object' ? item[this.itemValue] : item;
    },
    
    handleChange() {
      this.$emit('input', this.selectedItems);
      this.$emit('change', this.selectedItems);
    },
    
    clearSelection() {
      this.selectedItems = this.multiple ? [] : null;
      this.searchQuery = '';
      this.filterCriteria = 'contain';
      this.$emit('input', this.selectedItems);
      this.$emit('change', this.selectedItems);
      this.$emit('clear');
    },
    
    removeItem(index) {
      if (this.multiple && index > -1) {
        const newSelected = [...this.selectedItems];
        newSelected.splice(index, 1);
        this.selectedItems = newSelected;
        this.handleChange();
      }
    },
    
    applySearch() {
      this.currentPage = 0;
      this.preventSearch = true;
      
      console.log('Search applied:', this.searchQuery, this.filterCriteria);
      
      this.$emit('search', {
        query: this.searchQuery.trim(),
        criteria: this.filterCriteria
      });
      
      setTimeout(() => {
        this.preventSearch = false;
      }, 300);
    },
    
    previousPage() {
      if (!this.isFirstPage) {
        this.currentPage--;
        
        if (this.remoteSearch) {
          this.$emit('page-change', {
            page: this.currentPage,
            size: this.pageSize
          });
        }
      }
    },
    
    nextPage() {
      if (!this.isLastPage) {
        this.currentPage++;
        
        if (this.remoteSearch) {
          this.$emit('page-change', {
            page: this.currentPage,
            size: this.pageSize
          });
        }
      }
    },
    
    reset() {
      try {
        this.selectedItems = this.multiple ? [] : null;
        this.searchQuery = '';
        this.filterCriteria = 'contain';
        this.currentPage = 0;
        
        this.$emit('input', this.selectedItems);
        this.$emit('change', this.selectedItems);
        this.$emit('clear');

        this.$forceUpdate();
        console.log('AdvancedFilterComponent reset called');
      } catch (error) {
        console.warn('Error in AdvancedFilterComponent reset:', error);
      }
    },
    
    resetByKey(key) {
      if (key === this.chipGroupId || !key) {
        this.reset();
      }
    }
  }
};
</script>

<style scoped>
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
}

.advanced-filter-select {
  width: 100%;
}

.full-width-label {
  display: inline-block;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.filter-options {
  width: 100%;
  overflow: visible;
  padding: 8px !important;
}

.filter-row {
  margin: 0;
  width: 100%;
}

.criteria-select, .search-field {
  width: 100%;
  margin: 0;
}

.search-icon {
  cursor: pointer;
}

.chip-container {
  overflow: visible;
  width: 100%;
  flex-wrap: wrap;
}

.chip-scroll-container {
  overflow-x: auto;
  display: flex;
  flex-wrap: nowrap;
  max-width: 100%;
}

/* Improve scrollbar appearance */
.chip-scroll-container::-webkit-scrollbar {
  height: 4px;
}

.chip-scroll-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 2px;
}

.chip-scroll-container::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 2px;
}

.chip-scroll-container::-webkit-scrollbar-thumb:hover {
  background: #aaa;
}

:deep([id^="chip-group__"] div.v-slide-group__content) {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: nowrap;
  width: 100%;
}

:deep([id^="chip-group__"] div.v-slide-group__wrapper) {
  overflow: hidden;
  width: 100%;
}

:deep(.v-select__slot) {
  min-height: 40px;
}

:deep(.v-text-field__slot) {
  min-height: 40px;
}

:deep(.v-input__slot) {
  min-height: 40px;
}

:deep(.v-select__selections) {
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
  max-width: calc(100% - 28px) !important;
}

:deep(.v-menu__content) {
  overflow-y: auto;
  max-height: 500px !important;
}
</style>
